import React, { useContext, useState } from "react";
import Popup from "~/assets/js/components/Popup";
import i18n from "~/assets/js/Helpers/i18n";
import Notification from "~/assets/js/components/Notification";
import Timeslot from "~/assets/js/components/Timeslot";
import Button from "~/assets/js/components/Button";
import OverlayLoader from "~/assets/js/components/Loaders/OverlayLoader";
import isEmpty from "lodash.isempty";
import useAreaCookie from "~/assets/js/Helpers/hooks/useAreaCookie";
import PropTypes from "prop-types";

import { formatDate } from "../TimeSlotSelect/formatDateTime";
import { CheckoutContext, ACTIONS, PAGES } from "../../CheckoutProvider";
import TimeSlotSelectInfo from "../TimeSlotSelect/TimeSlotSelectInfo";
import { useCartContext } from "../../../../global/cartContext";
import useGroupOrder from "~/assets/js/global/useGroupOrder";
import { formatTimeslotEntryFromCart } from "~/assets/js/Helpers/timeslot-utils";

const NotAvailableNotification = () => {
    const cookie = useAreaCookie();
    const isDelivery = cookie.area.type === "delivery";
    const title = isDelivery
        ? i18n.t("overview.timeslotEdit.notification.deliveryTitle")
        : i18n.t("overview.timeslotEdit.notification.pickupTitle");

    return (
        <Notification icon={true} title={title} type="danger">
            <p style={{ margin: "0" }}>
                {i18n.t("overview.timeslotEdit.notification.content")}
            </p>
        </Notification>
    );
};

const TimeSlotEdit = ({ slotTaken, nextDeliverySlot, handleClose }) => {
    const { actions } = useCartContext();
    const { isGroupOrder, groupCartData } = useGroupOrder();
    const context = useContext(CheckoutContext);
    const { deliveryDay } = context.state.timeSlot;
    const cookie = useAreaCookie();
    const isDelivery = cookie.area.type === "delivery";

    const [state, setState] = useState({
        groupedDeliveryTimes: {},
        deliverySlot: {},
        selectedDeliverySlot: {},
        slotTaken: slotTaken,
        infoOpen: false,
    });

    const formattedDate = formatDate(deliveryDay);

    const groupOrderTimeslot =
        isGroupOrder &&
        formatTimeslotEntryFromCart(
            groupCartData?.slot_begin,
            groupCartData?.slot_end
        );

    const handleInfo = () => {
        setState({ ...state, infoOpen: true });
    };

    const handleCloseInfo = () => {
        setState({ ...state, infoOpen: false });
    };

    const handleChange = () => {
        setState({ ...state, selectedDeliverySlot: nextDeliverySlot });
    };

    const handleSave = async () => {
        await actions.updateTimeSlot(state.selectedDeliverySlot);

        context.dispatch({
            type: ACTIONS.ADD_TIME_SLOT,
            payload: state.selectedDeliverySlot,
        });

        handleClose();
    };

    const backToTimeSelect = () => {
        context.dispatch({
            type: ACTIONS.CHANGE_PAGE,
            payload: PAGES.TIME_SLOT_SELECT,
        });
    };

    const PopupConfig = {
        title: isDelivery
            ? i18n.t("overview.timeslotEdit.deliveryHeader")
            : i18n.t("overview.timeslotEdit.pickupHeader"),
        subtitle: isGroupOrder
            ? groupOrderTimeslot
            : `${formattedDate.append}, den ${formattedDate.date}`,
        customStyle: "time-slot-edit",
        open: slotTaken,
        isInfo: true,
        handleInfo: handleInfo,
        handleClose: handleClose,
        notification: isEmpty(state.selectedDeliverySlot) ? (
            <NotAvailableNotification />
        ) : (
            <></>
        ),
    };

    if (slotTaken && isEmpty(nextDeliverySlot)) {
        backToTimeSelect();
        return null;
    }

    return (
        <Popup {...PopupConfig}>
            <TimeSlotSelectInfo
                open={state.infoOpen}
                handleClose={handleCloseInfo}
            />
            {isEmpty(nextDeliverySlot) ? (
                <OverlayLoader />
            ) : (
                <div className="timeslot-edit">
                    <Timeslot
                        time={nextDeliverySlot}
                        availability={nextDeliverySlot.capacity}
                        isFirst={true}
                        isFirstContent={
                            isDelivery
                                ? i18n.t(
                                      "overview.timeslotEdit.nextDeliveryTime"
                                  )
                                : i18n.t("overview.timeslotEdit.nextPickupTime")
                        }
                        onValueChange={handleChange}
                    />
                    <p
                        className="timeslot-edit__back"
                        onClick={backToTimeSelect}
                    >
                        {i18n.t("overview.timeslotEdit.backToTimeSelect")}
                    </p>

                    <Button
                        disabled={isEmpty(state.selectedDeliverySlot)}
                        label={i18n.t("cart.continue")}
                        onClick={handleSave}
                    />
                </div>
            )}
        </Popup>
    );
};

TimeSlotEdit.propTypes = {
    slotTaken: PropTypes.any,
    nextDeliverySlot: PropTypes.any,
    handleClose: PropTypes.func,
};

export default TimeSlotEdit;
